import { z } from "zod";

/*
 * Warenkorb einlesen. Aus dem Text eines Warenkorbs (aus PDF, Screenshot oder eingefügt) entstehen Positionen für einen Beleg.
 * Nach festen Regeln, ohne KI-Dienst. Was unsicher ist, trägt einen Hinweis; der Mensch prüft vor der Übernahme.
 */

export interface CartLine {
  name: string;
  quantity: number;
  /** Einzelpreis netto in Cent. */
  unitPriceCents: number;
  vatRate: 0 | 7 | 19;
  /** Hinweis für die Prüfung, zum Beispiel „Preis bitte prüfen“. */
  note: string | null;
}

export const cartTextSchema = z.object({ text: z.string().trim().min(1, "Es wurde kein Text gefunden.").max(60_000, "Der Text ist zu lang.") });

const NUM = String.raw`\d{1,3}(?:\.\d{3})*,\d{2}|\d+,\d{2}|\d+\.\d{2}(?!\d)`;
const PRICE_G = new RegExp(String.raw`(?:€\s*(${NUM})|(${NUM})\s*(?:€|eur\b|euro\b))`, "gi");
const SUMMARY = /(zwischen)?summe|gesamt|total|versand|lieferung|porto|mwst|mehrwertsteuer|\bust\b|umsatzsteuer|steuer|gutschein|zahlart|zahlung|endbetrag|rechnungsbetrag|nachlass|ersparnis|ihre ersparnis/i;
const SKIP_LINE = /^(art(ikel)?\.?[\s-]*(nr|nummer)?|sku|ean|best(ell)?\.?[\s-]*(nr|nummer))\b[.:\s#-]*[\w-]*$/i;
const SKIP_NAME = /^(art(ikel)?\.?\s*-?\s*(nr|nummer)|artikelnr|sku|best(ell)?\.?\s*-?\s*nr|ean|menge|anzahl|preis|einzelpreis|gesamtpreis|produkt|artikel|position|pos\.?|stück|stk)\b[.:\s]*$/i;

const toCents = (s: string) => Math.round(Number(s.replace(/\./g, "").replace(",", ".")) * 100);

function pricesIn(line: string): { cents: number[]; rest: string } {
  const cents: number[] = [];
  const rest = line.replace(PRICE_G, (_m, a: string | undefined, b: string | undefined) => {
    const v = a ?? b;
    if (v) cents.push(v.includes(",") ? toCents(v) : Math.round(Number(v) * 100));
    return " ";
  });
  return { cents, rest };
}

function qtyIn(text: string): { quantity: number | null; rest: string; times: boolean } {
  let q: number | null = null;
  let times = false;
  let rest = text;
  const take = (re: RegExp) => {
    const m = rest.match(re);
    if (m && q === null) {
      q = Number(m[1]);
      rest = rest.replace(m[0], " ");
    }
  };
  take(/(?:^|\s)(\d{1,4})\s*(?:x|×|mal)(?=\s|$)/i);
  times = q !== null;
  take(/(?:menge|anzahl)\s*:?\s*(\d{1,4})\b/i);
  take(/(?:^|\s)(\d{1,4})\s*(?:stk\.?|stück|stueck)(?=\s|$)/i);
  take(/(?:^|\s)(?:x|×)\s*(\d{1,4})(?=\s|$)/i);
  return { quantity: q && q > 0 ? q : null, rest, times };
}

const clean = (s: string) => s.replace(/\s+/g, " ").replace(/^[\s:–—\-•*|·]+|[\s:–—\-•*|·]+$/g, "").trim();
const hasName = (s: string) => (s.match(/[A-Za-zÄÖÜäöüß]/g)?.length ?? 0) >= 3;

export function parseCartText(raw: string, opts: { pricesAre: "gross" | "net"; vatRate?: 0 | 7 | 19 } = { pricesAre: "gross" }): CartLine[] {
  const vat = opts.vatRate ?? 19;
  const lines = raw.split(/\r?\n/).map(clean).filter(Boolean);
  const out: CartLine[] = [];
  let pendingName: string[] = [];
  let pendingQty: number | null = null;

  for (const line of lines) {
    const { cents, rest: afterPrice } = pricesIn(line);
    if (cents.length === 0) {
      const q = qtyIn(line);
      const left = clean(q.rest);
      if (q.quantity !== null && !hasName(left)) {
        pendingQty = q.quantity;
        continue;
      }
      if (/^\d{1,4}$/.test(line)) {
        pendingQty = Number(line);
        continue;
      }
      if (hasName(line) && !SKIP_LINE.test(line) && !SKIP_NAME.test(line) && !SUMMARY.test(line)) pendingName = [...pendingName.slice(-1), clean(line)];
      continue;
    }
    if (SUMMARY.test(line)) {
      pendingName = [];
      pendingQty = null;
      continue;
    }
    const q = qtyIn(afterPrice);
    let name = clean(q.rest.replace(/\b(einzelpreis|gesamtpreis|preis|je|pro|stk\.?|stück)\b[.:]?/gi, " "));
    const quantity = q.quantity ?? pendingQty ?? 1;
    if (!hasName(name)) name = pendingName[pendingName.length - 1] ?? "";
    pendingName = [];
    pendingQty = null;
    if (!hasName(name)) continue;

    let unit = cents[0]!;
    let note: string | null = null;
    if (cents.length >= 2) {
      const last = cents[cents.length - 1]!;
      if (quantity > 1) {
        if (Math.abs(unit * quantity - last) > 2) {
          if (Math.abs(last * quantity - unit) <= 2) {
            unit = last;
          } else note = "Preis bitte prüfen";
        }
      } else if (last !== unit) {
        unit = last;
        note = "Mehrere Preise gefunden, bitte prüfen";
      }
    } else if (quantity > 1 && !q.times) {
      note = "Einzel- oder Gesamtpreis? Bitte prüfen";
    }
    const net = opts.pricesAre === "gross" ? Math.round((unit * 100) / (100 + vat)) : unit;
    out.push({ name: name.slice(0, 200), quantity, unitPriceCents: net, vatRate: vat, note });
  }
  return out;
}
